import { useState } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import { useToast, humanizeError } from '../shared/Toast'
import TransportationField from './TransportationField'
import { encodeTransport } from '../../lib/transportation'

const todayISO = () => new Date().toLocaleDateString('en-CA')

/**
 * Stripped-down appointment capture for the quick-add sheet: just enough to
 * get it on the calendar. The full form on the Appointments page still owns
 * specialty, location, and the post-visit fields.
 */
export default function QuickAppointmentForm({ recipientId, onDone, onCancel }) {
  const { user } = useAuth()
  const toast = useToast()
  const [f, setF] = useState({
    doctor_name: '',
    appointment_date: todayISO(),
    appointment_time: '',
    purpose: '',
  })
  const [transport, setTransport] = useState({ transportation: null, assigned_to_name: null })
  const [busy, setBusy] = useState(false)
  const set = (k, v) => setF((c) => ({ ...c, [k]: v }))

  async function save() {
    if (!f.doctor_name.trim()) return toast.error('Who is the appointment with?')
    if (!f.appointment_date) return toast.error('Please pick a date.')
    setBusy(true)
    const { error } = await supabase.from('appointments').insert({
      care_recipient_id: recipientId,
      created_by: user.id,
      doctor_name: f.doctor_name.trim(),
      appointment_date: f.appointment_date,
      appointment_time: f.appointment_time || null,
      purpose: f.purpose.trim() || null,
      pre_appointment_notes: encodeTransport({
        transportation: transport.transportation,
        assigned_to_name: transport.assigned_to_name,
        notes: '',
      }),
    })
    setBusy(false)
    if (error) return toast.error(humanizeError(error))
    toast.success('Appointment added.')
    onDone?.()
  }

  return (
    <div className="stack">
      <div className="spread">
        <h2 className="title" style={{ fontSize: 22 }}>Add appointment</h2>
        <button
          onClick={onCancel}
          aria-label="Close"
          style={{ background: 'none', border: 'none', color: 'var(--muted)', fontSize: 24, lineHeight: 1 }}
        >
          ×
        </button>
      </div>

      <div className="field">
        <label className="label" htmlFor="qa-name">Doctor or person</label>
        <input
          id="qa-name" className="input" value={f.doctor_name}
          onChange={(e) => set('doctor_name', e.target.value)}
          placeholder="e.g. Dr. Patel"
        />
      </div>

      <div className="row" style={{ gap: 10 }}>
        <div className="field grow">
          <label className="label" htmlFor="qa-date">Date</label>
          <input
            id="qa-date" type="date" className="input"
            value={f.appointment_date} onChange={(e) => set('appointment_date', e.target.value)}
          />
        </div>
        <div className="field grow">
          <label className="label" htmlFor="qa-time">Time</label>
          <input
            id="qa-time" type="time" className="input"
            value={f.appointment_time} onChange={(e) => set('appointment_time', e.target.value)}
          />
        </div>
      </div>

      <div className="field">
        <label className="label" htmlFor="qa-purpose">
          Purpose <span className="hint">— optional</span>
        </label>
        <input
          id="qa-purpose" className="input" value={f.purpose}
          onChange={(e) => set('purpose', e.target.value)}
          placeholder="What is this visit for?"
        />
      </div>

      <TransportationField
        recipientId={recipientId}
        value={transport.transportation}
        assignedName={transport.assigned_to_name}
        onChange={(transportation, assigned_to_name) => setTransport({ transportation, assigned_to_name })}
      />

      <button className="btn btn-block btn-lg" disabled={busy} onClick={save}>
        {busy ? <span className="spinner" /> : 'Save appointment'}
      </button>
    </div>
  )
}
